export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/v1'

let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
  if (token) {
    localStorage.setItem('access_token', token)
  } else {
    localStorage.removeItem('access_token')
  }
}

export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem('access_token')
  }
  return accessToken
}

export function clearTokens() {
  accessToken = null
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

interface RequestOptions {
  method?: string
  body?: FormData | File | object | string
  headers?: Record<string, string>
  auth?: boolean
}

function serializeBody(body: FormData | File | object | string): BodyInit {
  if (body instanceof FormData || body instanceof File || typeof body === 'string') {
    return body
  }
  return JSON.stringify(body)
}

function buildHeaders(
  body: FormData | File | object | string | undefined,
  overrides: Record<string, string>
): Record<string, string> {
  const usesJsonBody = body !== undefined && !(body instanceof FormData) && !(body instanceof File)
  const base: Record<string, string> = usesJsonBody ? { 'Content-Type': 'application/json' } : {}
  return { ...base, ...overrides }
}

export class MfaRequiredError extends Error {
  readonly code = 'mfa_required'
  readonly status = 401
  constructor() {
    super('MFA verification required')
    this.name = 'MfaRequiredError'
  }
}

export function redirectToLogin() {
  if (typeof window === 'undefined') return
  const pathname = window.location.pathname
  if (
    pathname.startsWith('/login/mfa') ||
    pathname === '/login' ||
    pathname.match(/^\/o\/[^/]+\/login/)
  ) {
    return
  }
  const brandedSlug = sessionStorage.getItem('branded_slug')
  if (brandedSlug) {
    window.location.href = `/o/${brandedSlug}/login`
  } else {
    window.location.href = '/login'
  }
}

function throwWithStatus(message: string, status: number): never {
  const error = new Error(message) as Error & { status: number }
  error.status = status
  throw error
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, auth = true } = options
  const requestHeaders: Record<string, string> = buildHeaders(body, headers)
  if (auth) {
    const token = getAccessToken()
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`
    }
  }
  const serialized = body !== undefined ? serializeBody(body) : undefined
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: requestHeaders,
    body: serialized,
    credentials: 'include',
  })
  if (response.status === 401) {
    const errorBody = await response.json().catch(() => ({} as Record<string, unknown>))
    if (errorBody.code === 'mfa_required') {
      throw new MfaRequiredError()
    }
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      requestHeaders['Authorization'] = `Bearer ${getAccessToken()}`
      const retryResponse = await fetch(`${API_BASE}${path}`, {
        method,
        headers: requestHeaders,
        body: serialized,
        credentials: 'include',
      })
      if (!retryResponse.ok) {
        const retryBody = await retryResponse.json().catch(() => ({ error: 'Request failed' }))
        throwWithStatus(retryBody.error || `API error: ${retryResponse.status}`, retryResponse.status)
      }
      return retryResponse.json()
    }
    clearTokens()
    redirectToLogin()
    throwWithStatus('Session expired. Please log in again.', 401)
  }
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: 'Request failed' }))
    throwWithStatus(errorBody.error || `API error: ${response.status}`, response.status)
  }
  if (response.status === 204) return undefined as T
  return response.json()
}

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) return false

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) return false

    const data = await response.json()
    setAccessToken(data.token)
    if (data.refreshToken) {
      localStorage.setItem('refresh_token', data.refreshToken)
    }
    return true
  } catch {
    return false
  }
}
